import { styled } from '@linaria/react';
import { Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { SessionPullRequest } from '../../../../api/agent-miner-client/agent-miner-client.type';
import {
  inkDarkest,
  inkLight,
  primaryBase,
  purpleBase,
  secondaryBase,
  secondaryLighter,
  skyDark,
  skyLight,
  skyLighter,
  yellowDarkest,
} from '../../../../styles/design-tokens';
import { AGENT_SOURCES_LABELS } from '../../../agentic-process/agentic-process.type';
import { AgentSourceIcon } from '../../shared/agent-source-icon';
import { fmtNum } from '../../shared/formatters';
import { StatTile } from '../../shared/stat-tile';
import { Card, clampPct, NotTraced, SectionTitle, SubtleText, ThisBadge } from './pr-detail.helpers';
import {
  baseNameOf,
  contributionCounts,
  contributorRows,
  matrixKey,
  sessionFileMatrix,
  sourceSplit,
} from './pr-survival.helpers';

const SOURCE_COLORS = [secondaryBase, purpleBase, yellowDarkest, secondaryLighter, skyDark];
const MATRIX_MAX_FILES = 8;

type PrContributorsViewProps = {
  pr: SessionPullRequest;
};

/**
 * The Contributors view of a pull request: headline counts, the whole-PR
 * authorship split and the viewed session's survival bar, a table with one row
 * per contributing session, the agent-source split, and (when several sessions
 * contributed) a session × file grid of matched lines. The session list itself
 * lives in the side panel.
 *
 * @param {PrContributorsViewProps} props - The selected pull request
 * @returns {JSX.Element} The contributors view
 */
export function PrContributorsView({ pr }: PrContributorsViewProps) {
  const { t } = useTranslation();
  const agentPct = pr.whole_pr.ai_percentage;
  const hasAuthorshipBar = agentPct !== null && agentPct !== undefined;
  const survivalRate = pr.survival.survival_rate;
  const counts = contributionCounts(pr);
  const rows = contributorRows(pr);
  const sources = sourceSplit(pr);
  const matrix = sessionFileMatrix(pr);
  // Cap the columns rather than dropping the matrix entirely for wide PRs — a
  // large PR is exactly where the session×file view earns its keep.
  const visibleMatrixFiles = matrix.files.slice(0, MATRIX_MAX_FILES);
  const showMatrix = matrix.sessions.length > 1 && visibleMatrixFiles.length > 0;
  const humanLines = Math.max(0, pr.whole_pr.total_additions - pr.whole_pr.total_ai_additions);

  return (
    <Stack>
      <TileGrid>
        <StatTile
          iconName="code"
          label={t('agent_miner.pull_requests.contributors.lines_tile')}
          value={t('agent_miner.pull_requests.contributors.lines_value', {
            agent: fmtNum(pr.whole_pr.total_ai_additions),
            human: fmtNum(humanLines),
          })}
          truncate
        />
        <StatTile
          iconName="description"
          label={t('agent_miner.pull_requests.contributors.files_tile')}
          value={t('agent_miner.pull_requests.contributors.files_value', {
            count: fmtNum(counts.filesWithAgentLines),
            total: fmtNum(pr.changed_files),
          })}
          truncate
        />
        <StatTile
          iconName="group"
          label={t('agent_miner.pull_requests.contributors.contributions_tile')}
          value={t('agent_miner.pull_requests.contributors.contributions_value', {
            sessions: fmtNum(counts.sessions),
            edits: fmtNum(counts.edits),
          })}
          truncate
        />
      </TileGrid>

      <Card>
        <Group justify="space-between" align="center" wrap="wrap">
          <SectionTitle>{t('agent_miner.pull_requests.agent_authorship')}</SectionTitle>
          <SubtleText>
            {t('agent_miner.pull_requests.fed_by', {
              count: pr.contributing_sessions.length,
              files: pr.files.length,
            })}
          </SubtleText>
        </Group>

        {hasAuthorshipBar && (
          <BarBlock>
            <BarHead>
              <BarLabel>{t('agent_miner.pull_requests.lines_authored')}</BarLabel>
              <BarStat>
                {t('agent_miner.pull_requests.agent')} {Math.round(clampPct(agentPct))}%
              </BarStat>
            </BarHead>
            <SplitTrack>
              <Segment $width={`${clampPct(agentPct)}%`} $color={secondaryBase} />
              <Segment $width={`${clampPct(100 - agentPct)}%`} $color={skyDark} />
            </SplitTrack>
          </BarBlock>
        )}

        <BarBlock>
          <BarHead>
            <BarLabel>{t('agent_miner.pull_requests.survived_to_merge')}</BarLabel>
            {pr.survival.line_precise ? (
              <BarStat>
                {fmtNum(pr.survival.survived)} / {fmtNum(pr.survival.authored)}
              </BarStat>
            ) : (
              <NotTraced>{t('agent_miner.pull_requests.not_line_traced')}</NotTraced>
            )}
          </BarHead>
          {pr.survival.line_precise && (
            <>
              <SplitTrack>
                <Segment $width={`${clampPct(survivalRate)}%`} $color={primaryBase} />
              </SplitTrack>
              <SurvivalPct>{Math.round(clampPct(survivalRate))}%</SurvivalPct>
            </>
          )}
          <BarSubtitle>{t('agent_miner.pull_requests.survived_subtitle')}</BarSubtitle>
        </BarBlock>

        {sources.length > 0 && (
          <BarBlock>
            <BarHead>
              <BarLabel>{t('agent_miner.pull_requests.contributors.by_source')}</BarLabel>
            </BarHead>
            <SplitTrack aria-hidden="true">
              {sources.map((segment, index) => (
                <Segment
                  key={segment.source}
                  $width={`${segment.pct}%`}
                  $color={SOURCE_COLORS[index % SOURCE_COLORS.length]}
                />
              ))}
            </SplitTrack>
            <LegendRow>
              {sources.map((segment, index) => (
                <LegendItem key={segment.source}>
                  <Swatch $color={SOURCE_COLORS[index % SOURCE_COLORS.length]} />
                  {t('agent_miner.pull_requests.contributors.source_segment', {
                    label: AGENT_SOURCES_LABELS[segment.source] ?? segment.source,
                    lines: fmtNum(segment.lines),
                  })}
                </LegendItem>
              ))}
            </LegendRow>
          </BarBlock>
        )}
      </Card>

      {rows.length > 0 && (
        <Card>
          <SectionTitle>{t('agent_miner.pull_requests.views.contributors')}</SectionTitle>
          <TableScroll>
            <Table aria-label={t('agent_miner.pull_requests.views.contributors')}>
              <thead>
                <tr>
                  <Th>{t('agent_miner.pull_requests.contributors.col_session')}</Th>
                  <Th>{t('agent_miner.pull_requests.contributors.col_user')}</Th>
                  <Th>{t('agent_miner.pull_requests.contributors.col_source')}</Th>
                  <ThNumber>{t('agent_miner.pull_requests.contributors.col_files')}</ThNumber>
                  <ThNumber>{t('agent_miner.pull_requests.contributors.col_edits')}</ThNumber>
                  <ThNumber>{t('agent_miner.pull_requests.contributors.col_lines')}</ThNumber>
                  <ThNumber>{t('agent_miner.pull_requests.contributors.col_share')}</ThNumber>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const clickable = !row.is_current;
                  return (
                    <tr key={row.session_id}>
                      <Td>
                        <SessionCell>
                          <AgentSourceIcon source={row.source} size={14} />
                          {clickable ? (
                            <SessionLink
                              href={`/application/agent-miner/sessions/${row.session_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={row.title ?? ''}
                            >
                              {row.title}
                            </SessionLink>
                          ) : (
                            <SessionName title={row.title ?? ''}>{row.title}</SessionName>
                          )}
                          {row.is_current && <ThisBadge>{t('agent_miner.pull_requests.this_session')}</ThisBadge>}
                        </SessionCell>
                      </Td>
                      <Td>{row.user ?? '—'}</Td>
                      <Td>{AGENT_SOURCES_LABELS[row.source] ?? row.source}</Td>
                      <TdNumber>{row.files === null ? '—' : fmtNum(row.files)}</TdNumber>
                      <TdNumber>{row.edits === null ? '—' : fmtNum(row.edits)}</TdNumber>
                      <TdNumber>{fmtNum(row.lines)}</TdNumber>
                      <TdNumber>
                        <ShareCell>
                          <ShareTrack aria-hidden="true">
                            <ShareFill $width={`${row.share}%`} />
                          </ShareTrack>
                          {row.share}%
                        </ShareCell>
                      </TdNumber>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        </Card>
      )}

      {showMatrix && (
        <Card>
          <SectionTitle>{t('agent_miner.pull_requests.contributors.matrix_title')}</SectionTitle>
          <TableScroll>
            <Table aria-label={t('agent_miner.pull_requests.contributors.matrix_title')}>
              <thead>
                <tr>
                  <Th>{t('agent_miner.pull_requests.contributors.col_session')}</Th>
                  {visibleMatrixFiles.map((file) => (
                    <ThNumber key={file.change_request_file_id} title={file.filename ?? ''}>
                      {baseNameOf(file.filename)}
                    </ThNumber>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.sessions.map((session) => (
                  <tr key={session.session_id}>
                    <Td>
                      <SessionCell>
                        <SessionName title={session.title ?? ''}>{session.title}</SessionName>
                        {session.is_current && <ThisBadge>{t('agent_miner.pull_requests.this_session')}</ThisBadge>}
                      </SessionCell>
                    </Td>
                    {visibleMatrixFiles.map((file) => {
                      const value = matrix.cells.get(matrixKey(session.session_id, file.change_request_file_id));
                      return (
                        <TdNumber key={file.change_request_file_id} $muted={value === undefined}>
                          {value === undefined ? '·' : value === null ? '—' : fmtNum(value)}
                        </TdNumber>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </Card>
      )}
    </Stack>
  );
}

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

const BarBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BarHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BarLabel = styled.span`
  font: 600 11px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const BarStat = styled.span`
  font: 500 11.5px/1 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  font-variant-numeric: tabular-nums;
`;

const SplitTrack = styled.div`
  display: flex;
  height: 22px;
  border-radius: 6px;
  overflow: hidden;
  background: ${skyLighter};
`;

const Segment = styled.div<{ $width: string; $color: string }>`
  height: 100%;
  width: ${(props) => props.$width};
  background: ${(props) => props.$color};
`;

const SurvivalPct = styled.span`
  font: 600 13px/1 'Figtree', sans-serif;
  color: ${primaryBase};
  font-variant-numeric: tabular-nums;
`;

const BarSubtitle = styled.span`
  font: 400 11px/1.4 'Figtree', sans-serif;
  color: ${skyDark};
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  font: 500 11px/1 'Figtree', sans-serif;
  color: ${inkLight};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Swatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${(props) => props.$color};
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font: 500 12px/1.4 'Figtree', sans-serif;
  color: ${inkDarkest};

  th,
  td {
    padding: 8px 10px;
    border-bottom: 1px solid ${skyLight};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const Th = styled.th`
  text-align: left;
  font: 600 10.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
`;

const ThNumber = styled(Th)`
  text-align: right;
`;

const Td = styled.td`
  text-align: left;
  vertical-align: middle;
`;

const TdNumber = styled.td<{ $muted?: boolean }>`
  text-align: right;
  vertical-align: middle;
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  font-variant-numeric: tabular-nums;
  color: ${(props) => (props.$muted ? skyDark : inkDarkest)};
`;

const SessionCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

const SessionName = styled.span`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
`;

const SessionLink = styled.a`
  font-weight: 600;
  color: ${inkDarkest};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;

  &:hover {
    color: ${secondaryBase};
  }
`;

const ShareCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

const ShareTrack = styled.span`
  display: inline-block;
  width: 60px;
  height: 6px;
  border-radius: 3px;
  background: ${skyLighter};
  overflow: hidden;
`;

const ShareFill = styled.span<{ $width: string }>`
  display: block;
  height: 100%;
  width: ${(props) => props.$width};
  background: ${secondaryBase};
`;
